import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Check, Truck, X, RefreshCw, ExternalLink, Copy, Bell, BellOff, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { isSupported, getPermission, requestPermission, notify, wasPrompted } from '../../lib/notifications';

const STATUS_STYLES = {
  pending:   { label: 'Pending',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid:      { label: 'Paid',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  shipped:   { label: 'Shipped',   cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const POLL_INTERVAL_MS = 15000;

// --- Notification helpers extracted so the load() function stays small ---

const announceSingleOrder = (o) => {
  const fired = notify(
    `🛎️ New order ${o.reference}`,
    `${o.customer_name} · A$${o.total.toFixed(2)} (${o.items.length} item${o.items.length === 1 ? '' : 's'})`,
    { tag: `order-${o.id}`, requireInteraction: true },
  );
  if (!fired) toast.info(`New order ${o.reference} — A$${o.total.toFixed(2)}`);
};

const announceBatch = (newOnes) => {
  const total = newOnes.reduce((s, o) => s + o.total, 0);
  const fired = notify(
    `🛎️ ${newOnes.length} new orders`,
    `Total A$${total.toFixed(2)} — tap to review`,
    { tag: 'orders-batch', requireInteraction: true },
  );
  if (!fired) toast.info(`${newOnes.length} new orders`);
};

const announceNewOrders = (newOnes) => {
  if (newOnes.length === 0) return;
  if (newOnes.length === 1) announceSingleOrder(newOnes[0]);
  else announceBatch(newOnes);
};

// --- External effect bodies. Keeping these outside the component means
// each useEffect/useCallback body contains very few identifiers and stays
// trivial to reason about (and survives audit dep-array scrutiny). ---

const fetchOrders = async ({ filter, fromPoll, seenIdsRef, initialisedRef, setOrders, setLoading, silent }) => {
  if (!silent) setLoading(true);
  try {
    const params = filter === 'all' ? {} : { status: filter };
    const { data } = await api.get('/orders', { params });
    if (initialisedRef.current && fromPoll) {
      const newOnes = data.filter((o) => !seenIdsRef.current.has(o.id));
      announceNewOrders(newOnes);
    }
    data.forEach((o) => seenIdsRef.current.add(o.id));
    initialisedRef.current = true;
    setOrders(data);
  } catch {
    if (!silent) toast.error('Could not load orders');
  } finally {
    if (!silent) setLoading(false);
  }
};

const schedulePollingForFilter = (load) => {
  load({ silent: false, fromPoll: false });
  return setInterval(() => load({ silent: true, fromPoll: true }), POLL_INTERVAL_MS);
};

const schedulePermissionPrompt = (setNotifPerm) => {
  if (!isSupported() || wasPrompted() || getPermission() !== 'default') return null;
  return setTimeout(async () => {
    const p = await requestPermission();
    setNotifPerm(p);
    if (p === 'granted') toast.success('Notifications enabled');
  }, 800);
};

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [notifPerm, setNotifPerm] = useState(getPermission());
  const [query, setQuery] = useState('');
  const seenIdsRef = useRef(new Set());
  const initialisedRef = useRef(false);
  const pollIdRef = useRef(null);
  const permTimerRef = useRef(null);

  const load = useCallback((opts = {}) => fetchOrders({
    filter,
    fromPoll: !!opts.fromPoll,
    silent: !!opts.silent,
    seenIdsRef,
    initialisedRef,
    setOrders,
    setLoading,
  }), [filter, seenIdsRef, initialisedRef, setOrders, setLoading, fetchOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    seenIdsRef.current = new Set();
    initialisedRef.current = false;
    pollIdRef.current = schedulePollingForFilter(load);
    return () => clearInterval(pollIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, seenIdsRef, initialisedRef, pollIdRef, schedulePollingForFilter]);

  useEffect(() => {
    permTimerRef.current = schedulePermissionPrompt(setNotifPerm);
    return () => { if (permTimerRef.current) clearTimeout(permTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNotifPerm, permTimerRef, schedulePermissionPrompt]);

  const enableNotifications = async () => {
    const p = await requestPermission();
    setNotifPerm(p);
    if (p === 'granted') toast.success('Notifications enabled');
    else if (p === 'denied') toast.error('Notifications blocked. Enable them in your browser settings.');
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      toast.success(`Order marked ${status}`);
      load({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Permanently delete order ${order.reference}?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success(`Order ${order.reference} deleted`);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const counts = filter === 'all' ? null : orders.length;

  const filteredOrders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) =>
      (o.reference || '').toLowerCase().includes(q)
      || (o.customer_email || '').toLowerCase().includes(q)
      || (o.customer_name || '').toLowerCase().includes(q)
      || (o.customer_phone || '').toLowerCase().includes(q)
    );
  }, [orders, query]);

  return (
    <div data-testid="admin-orders-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0a0a0a]">Orders {counts != null && <span className="text-slate-400 font-normal">· {counts}</span>}</h2>
          <p className="text-sm text-slate-500">Mark orders as paid once you see the PayID transfer in your bank account.</p>
        </div>
        <div className="flex items-center gap-2">
          {isSupported() && notifPerm !== 'granted' && (
            <button
              onClick={enableNotifications}
              data-testid="enable-notifications-btn"
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                notifPerm === 'denied'
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'border-red-200 text-red-700 hover:bg-red-50'
              }`}
              disabled={notifPerm === 'denied'}
              title={notifPerm === 'denied' ? 'Notifications blocked — enable in browser settings' : 'Enable notifications for new orders'}
            >
              {notifPerm === 'denied' ? <BellOff size={14} /> : <Bell size={14} />}
              {notifPerm === 'denied' ? 'Blocked' : 'Enable alerts'}
            </button>
          )}
          {notifPerm === 'granted' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg" title="Notifications on for new orders">
              <Bell size={13} /> Alerts on
            </span>
          )}
          <button onClick={() => load()} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-red-600 border border-slate-200 rounded-lg" data-testid="orders-refresh-btn">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1 mb-3 border-b border-slate-200">
        {['pending', 'paid', 'shipped', 'cancelled', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`orders-filter-${f}`}
            className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px capitalize transition-colors ${
              filter === f ? 'border-red-500 text-red-700' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by reference, name, email or phone…"
          data-testid="orders-search-input"
          className="w-full pl-9 pr-9 py-2.5 text-sm bg-white border border-slate-200 focus:border-red-400 focus:ring-2 focus:ring-red-100 rounded-lg outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear search"
            data-testid="orders-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-red-600 rounded"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-red-500" /></div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-sm text-slate-500" data-testid="orders-empty">
          {query
            ? <>No orders match <span className="font-mono text-[#0a0a0a]">"{query}"</span>.</>
            : <>No {filter === 'all' ? '' : filter} orders yet.</>
          }
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
          {filteredOrders.map((o) => (
            <OrderRow key={o.id} order={o} expanded={expanded === o.id} onToggle={() => setExpanded(expanded === o.id ? null : o.id)} onStatus={updateStatus} onDelete={deleteOrder} />
          ))}
        </div>
      )}
    </div>
  );
};

const OrderRow = ({ order, expanded, onToggle, onStatus, onDelete }) => {
  const status = STATUS_STYLES[order.status] || STATUS_STYLES.pending;
  const created = new Date(order.created_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

  const copyRef = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(order.reference);
      toast.success('Reference copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div>
      <button onClick={onToggle} className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-4" data-testid={`order-row-${order.reference}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono font-bold text-[#0a0a0a]">{order.reference}</span>
            <span
              role="button"
              tabIndex={0}
              onClick={copyRef}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') copyRef(e); }}
              className="text-slate-400 hover:text-red-600 inline-flex cursor-pointer"
              title="Copy reference"
            ><Copy size={12} /></span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${status.cls}`}>{status.label}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {order.customer_name} · {order.customer_email} · {created}
          </div>
        </div>
        <div className="text-right">
          <div className="font-semibold text-[#0a0a0a]">A${order.total.toFixed(2)}</div>
          <div className="text-[11px] text-slate-400">{order.items.length} item{order.items.length === 1 ? '' : 's'}</div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-4 bg-slate-50/60 border-t border-slate-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-400 mb-2">Customer</p>
              <div className="space-y-1 text-slate-700">
                <div><b>{order.customer_name}</b></div>
                <div><a href={`mailto:${order.customer_email}`} className="text-red-600 hover:underline">{order.customer_email}</a></div>
                <div>{order.customer_phone}</div>
              </div>
              <p className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-400 mt-4 mb-2">Shipping address</p>
              <div className="text-slate-700 whitespace-pre-line">{order.shipping_address}</div>
              {order.notes && (
                <>
                  <p className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-400 mt-4 mb-2">Notes</p>
                  <div className="text-slate-700 whitespace-pre-line">{order.notes}</div>
                </>
              )}
            </div>
            <div>
              <p className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-400 mb-2">Items</p>
              <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                {order.items.map((it) => (
                  <div key={it.product_id || `name-${it.name}`} className="flex justify-between gap-3 px-3 py-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-[#0a0a0a]">{it.name}</div>
                      <div className="text-xs text-slate-500">Qty {it.qty} · ${it.price.toFixed(2)}</div>
                    </div>
                    <div className="font-semibold text-[#0a0a0a]">${(it.price * it.qty).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
                {order.discount > 0 && <div className="flex justify-between text-red-700"><span>Discount</span><span>− ${order.discount.toFixed(2)}</span></div>}
                <div className="flex justify-between font-bold text-[#0a0a0a] pt-1 border-t border-slate-200"><span>Total</span><span>A${order.total.toFixed(2)}</span></div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2 justify-between items-center">
            <a href={`/pay/${order.reference}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-slate-600 hover:text-red-600 border border-slate-200 rounded-lg">
              <ExternalLink size={12} /> View payment page
            </a>
            <div className="flex flex-wrap gap-2">
              {order.status === 'pending' && (
                <>
                  <button onClick={() => onStatus(order.id, 'paid')} data-testid={`mark-paid-${order.reference}`} className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg">
                    <Check size={13} /> Mark paid
                  </button>
                  <button onClick={() => onStatus(order.id, 'cancelled')} className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-semibold rounded-lg">
                    <X size={13} /> Cancel
                  </button>
                </>
              )}
              {order.status === 'paid' && (
                <button onClick={() => onStatus(order.id, 'shipped')} className="inline-flex items-center gap-1.5 px-3 py-2 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold rounded-lg">
                  <Truck size={13} /> Mark shipped
                </button>
              )}
              {order.status === 'shipped' && (
                <button onClick={() => onStatus(order.id, 'paid')} className="inline-flex items-center gap-1.5 px-3 py-2 text-slate-500 text-xs">
                  Revert to paid
                </button>
              )}
              <button
                onClick={() => onDelete(order)}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-slate-400 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 text-xs font-semibold rounded-lg"
                data-testid={`delete-order-${order.reference}`}
                title="Permanently delete this order"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersTab;
